/**
 * HG12 route-per-page for the verification IA (#46665).
 *
 * WP1c scaffolded page families without registering TanStack paths. The shell
 * rail navigates to `/queue`, `/findings`, … — unregistered, those URLs fall
 * through `__root` into the old ProductChrome. Each NAV item and its `$id`
 * detail is a real route wrapped in VerificationShell.
 *
 * `/` stays `homeRoute` in singletons.tsx (WP11b). `/not-found` stays
 * verification-not-found-recovery.tsx (WP10d). `/workflows/new` is registered
 * before `/workflows/$id`. Each export is `createRoute({ path: "…" })` so
 * `test-route-tree-parity` can index the mounted identifiers.
 */

import type { ComponentType, ReactNode } from "react";
import { createRoute } from "@tanstack/react-router";
import { z } from "zod";
import { OBJECT_PEEK_OPEN_EVENT } from "@excellent/ui";
import { useNavigate, usePathname, useSearch } from "@excellent/ui/router";
import { rootRoute } from "./__root";
import { routeSearchSchema } from "../shell/search";
import { VerificationShell } from "../verification/shell";
import {
  Callout,
  Card,
  CardContent,
  DataTable,
  Mono,
  Section,
  type DataTableColumn,
} from "../verification/ui";
import {
  PageFrame,
  type VerificationPageProps,
} from "../verification/pages/page-frame";
import {
  FindingDetailPage,
  FindingsPage,
  QueuePage,
  RunDetailPage,
  RunsPage,
} from "../verification/pages/decide";
import { InboxPage } from "../verification/pages/inbox";
import { WorkLedgerPage } from "../verification/pages/work";
import {
  ControlDetailPage,
  ControlsPage,
  PoliciesPage,
  PolicyDetailPage,
} from "../verification/pages/define";
import {
  WorkflowDetailPage,
  WorkflowNewPage,
  WorkflowsPage,
} from "../verification/pages/workflows";
import { WorkDetailPage } from "../verification/pages/work-detail";
import {
  ArtifactDetailPage,
  ConnectorsPage,
  EvidencePage,
} from "../verification/pages/evidence";
import { EnvironmentsPage } from "../verification/pages/environments";
import { CheckResultProof } from "../verification/pages/check-result-proof";
import {
  AuditRoomPage,
  PackDetailPage,
  PacksPage,
  ReceiptDetailPage,
  ReceiptsPage,
} from "../verification/pages/prove";
import {
  ApiPage,
  PosturePage,
  RuntimePage,
  ScorecardPage,
  TrustPage,
  WorkersPage,
} from "../verification/pages/operate";
import {
  AnalyticsPage,
  AutonomyPage,
  OutcomesPage,
} from "../verification/pages/outcomes";
import { DemoLabPage } from "../verification/pages/demo-lab";
import { preserveEnvironmentHref } from "../verification/nav";
import { inspectorHrefForCurrentPage } from "../verification/inspector-object";

function ShellPage({ route, children }: { route: string; children: ReactNode }) {
  return (
    <div data-route={route} data-verification-ia="">
      <VerificationShell>{children}</VerificationShell>
    </div>
  );
}

function useGo() {
  const navigate = useNavigate();
  const pathname = usePathname();
  const search = useSearch();
  return (to: string) => {
    const currentSearch = search.toString();
    const destination = preserveEnvironmentHref(to, currentSearch);
    const inspectorHref = inspectorHrefForCurrentPage(pathname, currentSearch, destination);
    if (inspectorHref) {
      window.dispatchEvent(new CustomEvent(OBJECT_PEEK_OPEN_EVENT));
    }
    navigate.push(inspectorHref ?? destination);
  };
}

const workflowsSearchSchema = routeSearchSchema({
  reason: z.string().optional(),
});

function IaPage({
  route,
  Page,
}: {
  route: string;
  Page: ComponentType<VerificationPageProps>;
}) {
  const go = useGo();
  return (
    <ShellPage route={route}>
      <Page onNavigate={go} />
    </ShellPage>
  );
}

type GuideStep = {
  step: string;
  owner: string;
  evidence: string;
};

const GUIDE_STEPS: GuideStep[] = [
  {
    step: "Define",
    owner: "Workspace owner",
    evidence: "Controls, policies, and workflow predicates are named before any run starts.",
  },
  {
    step: "Prove",
    owner: "Collector or agent",
    evidence: "Evidence artifacts are admitted with hashes, lineage, freshness, and access state.",
  },
  {
    step: "Decide",
    owner: "Independent reviewer",
    evidence: "Findings, runs, and review cases record PASS, FAIL, or UNKNOWN with citations.",
  },
  {
    step: "Operate",
    owner: "Runtime owner",
    evidence: "Workers, keys, token scopes, scorecards, and posture checks keep the loop running.",
  },
];

const GUIDE_COLUMNS: DataTableColumn<GuideStep>[] = [
  { key: "step", header: "Step", render: (row) => <strong>{row.step}</strong> },
  { key: "owner", header: "Owner", render: (row) => row.owner },
  { key: "evidence", header: "Evidence", render: (row) => row.evidence },
];

type Criterion = {
  name: string;
  route: string;
  gate: string;
};

const BUYABLE_CRITERIA: Criterion[] = [
  { name: "A stranger can understand what is verified", route: "/guide", gate: "route-tree-parity" },
  { name: "A real workflow can be admitted or refused", route: "/workflows/new", gate: "verification-real-first-loop" },
  { name: "Evidence can be inspected without demo authority", route: "/evidence", gate: "verification-product-routes" },
  { name: "Receipts can be replayed and externally checked", route: "/receipts", gate: "verification-prove-contracts" },
  { name: "Operations expose failures deterministically", route: "/workers", gate: "verification-operate-contracts" },
];

const CRITERION_COLUMNS: DataTableColumn<Criterion>[] = [
  { key: "name", header: "Criterion", render: (row) => row.name },
  { key: "route", header: "Route", render: (row) => <Mono>{row.route}</Mono> },
  { key: "gate", header: "Gate", render: (row) => <Mono>{row.gate}</Mono> },
];

const DESIGN_RULES = [
  {
    title: "State is visible",
    body: "Every loading, empty, refused, and failed state says what is unavailable and which contract owns it.",
  },
  {
    title: "Evidence stays inspectable",
    body: "Hashes, ids, run refs, and receipt refs use tabular figures and wrap instead of clipping on narrow screens.",
  },
  {
    title: "Actions are explicit",
    body: "Mutable controls carry action ids and disabled reasons, so QA can prove a button is a real path or gated.",
  },
];

export function GuidePage({ onNavigate: _onNavigate }: VerificationPageProps) {
  return (
    <PageFrame
      page="guide"
      eyebrow="Reference"
      title="How it works"
      lede="The verification loop, the vocabulary, and the jobs — in the product rather than beside it."
    >
      <Callout tone="accent" title="The product loop">
        A claim is not accepted because an agent said it worked. It moves through
        contract-backed evidence, an independent decision, and an auditable receipt.
      </Callout>
      <Section title="Loop map" note="Reference content rendered in the product shell.">
        <DataTable columns={GUIDE_COLUMNS} rows={GUIDE_STEPS} rowKey={(row) => row.step} />
      </Section>
    </PageFrame>
  );
}

export function BuyablePage({ onNavigate: _onNavigate }: VerificationPageProps) {
  return (
    <PageFrame
      page="buyable"
      eyebrow="Reference"
      title="Is it buyable?"
      lede="The five things a stranger must be able to do, each with the gate that protects it."
    >
      <Section title="Acceptance scoreboard" note="Routes are canonical product routes, not archive pages.">
        <DataTable columns={CRITERION_COLUMNS} rows={BUYABLE_CRITERIA} rowKey={(row) => row.name} />
      </Section>
      <Callout tone="unknown" title="Interpretation">
        A criterion is not met by a screenshot alone. The named route must render
        through the product shell and the named gate must fail when the route,
        contract, or deterministic failure state is missing.
      </Callout>
    </PageFrame>
  );
}

export function DesignSystemPage({ onNavigate: _onNavigate }: VerificationPageProps) {
  return (
    <PageFrame
      page="design"
      eyebrow="Reference"
      title="Design system"
      lede="Verification shell tokens, primitives, and rules."
    >
      <Section title="Product rules" note="The reference now ships with the IA route.">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          {DESIGN_RULES.map((rule) => (
            <Card key={rule.title}>
              <CardContent className="flex h-full flex-col gap-2">
                <h2 className="type-title font-medium text-foreground">{rule.title}</h2>
                <p className="type-body text-muted-foreground">{rule.body}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </Section>
      <Callout tone="neutral" title="Authority">
        Product pages import primitives from <Mono>excellent/client/verification/ui.tsx</Mono>.
        Prototype modules stay archive-only.
      </Callout>
    </PageFrame>
  );
}

export const queueRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/queue",
  component: () => <IaPage route="verification-queue" Page={QueuePage} />,
});
export const inboxRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/inbox",
  component: () => <IaPage route="verification-inbox" Page={InboxPage} />,
});
export const findingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/findings",
  component: () => <IaPage route="verification-findings" Page={FindingsPage} />,
});
export const findingDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/findings/$id",
  component: function FindingDetail() {
    const go = useGo();
    const { id } = findingDetailRoute.useParams();
    return (
      <ShellPage route="verification-finding">
        <FindingDetailPage id={id} onNavigate={go} />
      </ShellPage>
    );
  },
});
export const runsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/runs",
  component: () => <IaPage route="verification-runs" Page={RunsPage} />,
});
export const runDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/runs/$id",
  component: function RunDetail() {
    const go = useGo();
    const { id } = runDetailRoute.useParams();
    return (
      <ShellPage route="verification-run">
        <RunDetailPage id={id} onNavigate={go} />
      </ShellPage>
    );
  },
});
export const workDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/work-ledger/$id",
  component: function WorkDetail() {
    const go = useGo();
    const { id } = workDetailRoute.useParams();
    return (
      <ShellPage route="verification-work-detail">
        <WorkDetailPage id={id} onNavigate={go} />
      </ShellPage>
    );
  },
});
export const workRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/work-ledger",
  component: () => <IaPage route="verification-work" Page={WorkLedgerPage} />,
});
export const workflowsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/workflows",
  validateSearch: workflowsSearchSchema,
  component: function Workflows() {
    const go = useGo();
    const { reason } = workflowsRoute.useSearch();
    return (
      <ShellPage route="verification-workflows">
        <WorkflowsPage onNavigate={go} reason={reason} />
      </ShellPage>
    );
  },
});
export const workflowNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/workflows/new",
  validateSearch: routeSearchSchema({ intent: z.string().trim().max(2_000).optional() }),
  component: function WorkflowNew() {
    const go = useGo();
    const { intent } = workflowNewRoute.useSearch();
    return (
      <ShellPage route="verification-workflow-new">
        <WorkflowNewPage onNavigate={go} initialIntent={intent} />
      </ShellPage>
    );
  },
});
export const workflowDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/workflows/$id",
  component: function WorkflowDetail() {
    const go = useGo();
    const { id } = workflowDetailRoute.useParams();
    return (
      <ShellPage route="verification-workflow">
        <WorkflowDetailPage id={id} onNavigate={go} />
      </ShellPage>
    );
  },
});
export const controlsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/controls",
  component: () => <IaPage route="verification-controls" Page={ControlsPage} />,
});
export const controlDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/controls/$id",
  component: function ControlDetail() {
    const go = useGo();
    const { id } = controlDetailRoute.useParams();
    return (
      <ShellPage route="verification-control">
        <ControlDetailPage id={id} onNavigate={go} />
      </ShellPage>
    );
  },
});
export const policiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/policies",
  component: () => <IaPage route="verification-policies" Page={PoliciesPage} />,
});
export const policyDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/policies/$id",
  component: function PolicyDetail() {
    const go = useGo();
    const { id } = policyDetailRoute.useParams();
    return (
      <ShellPage route="verification-policy">
        <PolicyDetailPage id={id} onNavigate={go} />
      </ShellPage>
    );
  },
});
export const evidenceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/evidence",
  component: () => <IaPage route="verification-evidence" Page={EvidencePage} />,
});
export const artifactDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/evidence/$id",
  component: function ArtifactDetail() {
    const go = useGo();
    const { id } = artifactDetailRoute.useParams();
    return (
      <ShellPage route="verification-artifact">
        <ArtifactDetailPage id={id} onNavigate={go} />
      </ShellPage>
    );
  },
});
export const connectorsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/connectors",
  component: () => <IaPage route="verification-connectors" Page={ConnectorsPage} />,
});
export const environmentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/environments",
  component: () => <IaPage route="verification-environments" Page={EnvironmentsPage} />,
});
export const receiptsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/receipts",
  component: () => <IaPage route="verification-receipts" Page={ReceiptsPage} />,
});
export const receiptDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/receipts/$id",
  component: function ReceiptDetail() {
    const go = useGo();
    const { id } = receiptDetailRoute.useParams();
    return (
      <ShellPage route="verification-receipt">
        <ReceiptDetailPage id={id} onNavigate={go} />
      </ShellPage>
    );
  },
});
export const checkResultDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/check-results/$id",
  component: function CheckResultDetail() {
    const { id } = checkResultDetailRoute.useParams();
    const parsedId = Number(id);
    return (
      <ShellPage route="verification-check-result">
        {Number.isSafeInteger(parsedId) && parsedId > 0 ? (
          <CheckResultProof resultRecordId={parsedId} />
        ) : (
          <PageFrame
            page="check-result"
            eyebrow="Proof"
            title="Check result unavailable"
            lede="The requested proof record id is not a valid retained check result."
          />
        )}
      </ShellPage>
    );
  },
});
export const packsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/packs",
  component: () => <IaPage route="verification-packs" Page={PacksPage} />,
});
export const packDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/packs/$id",
  component: function PackDetail() {
    const go = useGo();
    const { id } = packDetailRoute.useParams();
    return (
      <ShellPage route="verification-pack">
        <PackDetailPage id={id} onNavigate={go} />
      </ShellPage>
    );
  },
});
export const auditRoomRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/audit-room",
  component: () => <IaPage route="verification-audit-room" Page={AuditRoomPage} />,
});
export const workersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/workers",
  component: () => <IaPage route="verification-workers" Page={WorkersPage} />,
});
export const scorecardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/scorecard",
  component: () => <IaPage route="verification-scorecard" Page={ScorecardPage} />,
});
export const runtimeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/runtime",
  component: () => <IaPage route="verification-runtime" Page={RuntimePage} />,
});
export const sdkRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sdk",
  component: () => <IaPage route="verification-api" Page={ApiPage} />,
});
export const apiCatalogRoute = sdkRoute;
export const demoLabRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/demo-lab",
  component: () => <IaPage route="verification-demo-lab" Page={DemoLabPage} />,
});
export const trustRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/trust",
  component: () => <IaPage route="verification-trust" Page={TrustPage} />,
});
export const postureRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/posture",
  component: () => <IaPage route="verification-posture" Page={PosturePage} />,
});
export const outcomesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/outcomes",
  component: () => <IaPage route="verification-outcomes" Page={OutcomesPage} />,
});
export const autonomyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/autonomy",
  component: () => <IaPage route="verification-autonomy" Page={AutonomyPage} />,
});
export const analyticsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/analytics",
  component: () => <IaPage route="verification-analytics" Page={AnalyticsPage} />,
});
export const guideRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/guide",
  component: () => <IaPage route="verification-guide" Page={GuidePage} />,
});
export const buyableRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/buyable",
  component: () => <IaPage route="verification-buyable" Page={BuyablePage} />,
});
export const designRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/design",
  component: () => <IaPage route="verification-design" Page={DesignSystemPage} />,
});

/** `$id` children + `/workflows/new`. NAV list pages live in singletons.tsx. */
export const verificationIaRoutes = [
  findingDetailRoute,
  runDetailRoute,
  workDetailRoute,
  workflowNewRoute,
  workflowDetailRoute,
  controlDetailRoute,
  policyDetailRoute,
  artifactDetailRoute,
  receiptDetailRoute,
  checkResultDetailRoute,
  packDetailRoute,
];
